import React, { useState } from 'react';

const DraggableTimeline = ({ events, onReorder, onEdit, onDelete }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Handle sequence number change
  const handleSequenceChange = (currentIndex, newSequence) => {
    const targetIndex = parseInt(newSequence) - 1;
    
    // Validate
    if (isNaN(targetIndex) || targetIndex < 0 || targetIndex >= events.length || targetIndex === currentIndex) {
      return;
    }

    // Swap times between current and target
    const newEvents = [...events];
    const tempTime = newEvents[currentIndex].time;
    const tempDate = newEvents[currentIndex].date;
    
    newEvents[currentIndex] = {
      ...newEvents[currentIndex],
      time: newEvents[targetIndex].time,
      date: newEvents[targetIndex].date
    };
    
    newEvents[targetIndex] = {
      ...newEvents[targetIndex],
      time: tempTime,
      date: tempDate
    };
    
    // Now reorder positions
    const movedItem = newEvents[currentIndex];
    newEvents.splice(currentIndex, 1);
    newEvents.splice(targetIndex, 0, movedItem);
    
    onReorder(newEvents);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newEvents = [...events];
    
    // Swap times between dragged and target
    const tempTime = newEvents[draggedIndex].time;
    const tempDate = newEvents[draggedIndex].date;
    
    newEvents[draggedIndex] = {
      ...newEvents[draggedIndex],
      time: newEvents[index].time,
      date: newEvents[index].date
    };
    
    newEvents[index] = {
      ...newEvents[index],
      time: tempTime,
      date: tempDate
    };
    
    // Now reorder positions
    const draggedItem = newEvents[draggedIndex];
    newEvents.splice(draggedIndex, 1);
    newEvents.splice(index, 0, draggedItem);
    
    setDraggedIndex(index);
    onReorder(newEvents);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const getCategoryColor = (category) => {
    const colors = {
      preparation: 'from-rose-100 to-pink-200',
      photography: 'from-purple-100 to-lavender-200',
      ceremony: 'from-gold-100 to-yellow-200',
      reception: 'from-emerald-100 to-mint-200',
      entertainment: 'from-blue-100 to-cyan-200'
    };
    return colors[category] || 'from-gray-100 to-gray-200';
  };

  if (events.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4 opacity-50">📅</div>
        <p className="text-gray-600">No events in timeline yet</p>
        <p className="text-sm text-gray-500 mt-2">Generate timeline or add custom events</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {events.map((item, index) => {
        // Check if we need to show a date label (when date changes from previous item)
        const showDateLabel = item.dateLabel && (index === 0 || events[index - 1].dateLabel !== item.dateLabel);
        
        return (
          <React.Fragment key={index}>
            {/* Date Label Divider */}
            {showDateLabel && (
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gold-400 to-transparent"></div>
                <div className="px-4 py-2 bg-gradient-to-r from-gold-500 to-yellow-500 text-white rounded-full font-bold text-sm shadow-lg">
                  📅 {item.dateLabel} {item.date && `- ${new Date(item.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gold-400 to-transparent"></div>
              </div>
            )}
            
            <div
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`relative bg-gradient-to-r ${getCategoryColor(item.category)} rounded-2xl p-4 border border-gold-200/50 shadow-lg cursor-move transition-all duration-300 hover:shadow-xl hover:scale-102 ${
                draggedIndex === index ? 'opacity-50 scale-95' : ''
              }`}
            >
              {/* Sequence Number Input */}
              <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
                <div className="relative group">
                  <input
                    type="number"
                    min="1"
                    max={events.length}
                    value={index + 1}
                    onChange={(e) => handleSequenceChange(index, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-10 h-10 text-center font-bold text-gray-700 bg-white/90 border-2 border-gold-300 rounded-xl shadow-md hover:border-gold-500 focus:border-gold-600 focus:ring-2 focus:ring-gold-300 transition-all duration-200 cursor-pointer"
                    title="Click to change order"
                  />
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      Change order
                    </div>
                  </div>
                </div>
              </div>

          <div className="flex items-start space-x-4 ml-6">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-white/80 rounded-xl flex items-center justify-center shadow-md">
                <span className="text-2xl">{item.icon}</span>
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-bold text-gray-800 text-lg">{item.event}</h4>
                <span className="text-sm font-bold text-gray-600 bg-white/60 px-2 py-1 rounded-lg">
                  {item.time}
                </span>
              </div>
              <p className="text-gray-600 text-sm break-words">{item.description}</p>
              
              {/* Badges */}
              <div className="flex items-center gap-2 mt-2">
                {item.isCustom && (
                  <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">Custom</span>
                )}
                {item.isRitual && (
                  <span className="text-xs bg-purple-500 text-white px-2 py-1 rounded-full">Ritual</span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(item, index);
                  }}
                  className="text-blue-600 hover:text-blue-700 text-lg font-medium hover:scale-110 transition-transform"
                  title="Edit event"
                >
                  ✏️
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(index);
                  }}
                  className="text-red-600 hover:text-red-700 text-lg font-medium hover:scale-110 transition-transform"
                  title="Delete event"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>

          {/* Timeline connector */}
          {index < events.length - 1 && (
            <div className="absolute left-10 -bottom-2 w-0.5 h-4 bg-gold-300"></div>
          )}
        </div>
      </React.Fragment>
      );
      })}
    </div>
  );
};

export default DraggableTimeline;
